import _ from 'lodash'
import queryMapping from '@wix/document-services-json-schemas/dist/queryMapping.json'
import { createMaterializer, Update } from '@wix/materializer'
import { BaseDataItem, Component, Data, DataMapName } from '@wix/thunderbolt-becky-types'
import { parseSvgString } from '@wix/thunderbolt-commons'
import { createCompRef, MegaStore } from '..'
import { dataNodes as baseDataNodes } from '../dataNodes/dataNodes'
import { compQueriesRefs } from '../refs/compQueriesRefs'
import { COMPS_ROOT_NAMESPACE } from '../refs/constants'
import { createRefResolver } from '../refs/schemaRefResolver'
import { DataStore } from '../stores/DataStore'
import type {
	Batched,
	Catharsis,
	CatharsisArgs,
	UpdateData,
	UpdateEnvironment,
	UpdateStructure,
	UpdateSvgString,
} from '../types/catharsis.types'
import type { NodeRefs } from '../types/refs.types'
import type { CatharsisTraceValue } from '..'
import { defaultToViewItem, fixStructure, oldDefaultToViewItem } from './catharsisUtils'
import { createRepeatedItemOverrideId, getTemplateIdAndItemContext, isRepeatedItemOverrideId } from './repeatersAPI'
import { EMPTY_SCHEMA } from './EMPTY_SCHEMA'
import { updateTwoLevelRecords } from './updateTwoLevelRecords'

const mapNameToQuery = _.invert(queryMapping) as Record<keyof Data, string>
type UpdateDataDependencies = (
	update: Update<void>,
	type: DataMapName,
	id: string,
	dataItem: BaseDataItem | undefined
) => void

export const getCatharsis = ({
	onInvalidation,
	extensions,
	structureStore = new MegaStore(),
	materializerFactory = createMaterializer,
}: CatharsisArgs): Catharsis => {
	const dataStore = new DataStore()
	const originalDataIdToCompId: Record<string, string> = {}
	const { componentNodes = {}, schemas } = extensions
	const dataNodes = { ...baseDataNodes, ...extensions.dataNodes }
	let useNewDefaultToViewItem = false

	const defaultDataToViewItem = defaultToViewItem(schemas)

	let traceEnabled = false
	const catharsisTrace = [] as Array<CatharsisTraceValue>

	const updateTrace = (action: string, data: any) => {
		if (traceEnabled) {
			const traceValue = _.last(catharsisTrace)
			traceValue?.actions.push({ action, data })
		}
	}

	const materializer = materializerFactory({
		transform: (value: any, path: Array<string>) => {
			const [namespace, aspect, id] = path
			if (namespace === 'data') {
				const dataItem = dataStore.getData(aspect, id)

				if (typeof dataItem === 'undefined') {
					return undefined
				}

				if (dataNodes[dataItem.type]) {
					return dataNodes[dataItem.type].toViewItem(dataItem, value?.schema, value?.custom)
				}
				const toViewItem = useNewDefaultToViewItem ? defaultDataToViewItem : oldDefaultToViewItem
				return toViewItem(aspect, dataItem, value?.schema)
			}

			if (namespace === COMPS_ROOT_NAMESPACE && aspect !== 'modes') {
				const [compId, itemContext] = getTemplateIdAndItemContext(id)
				const contextStore = itemContext ? structureStore.getChildStore(itemContext) : structureStore
				const component = contextStore.getById(compId)
				return component && componentNodes[aspect].toViewItem(component, value)
			}

			return value
		},
	})

	const repeatedSymbol = Symbol('repeated')
	const refs = {
		..._.mapValues(componentNodes, (__, key) => (component: Component | string) =>
			createCompRef(key, typeof component === 'string' ? component : component.id)
		),
		...compQueriesRefs,
		modes: (modeId: string) => createCompRef('modes', modeId),
		[repeatedSymbol]: false,
	} as NodeRefs<any> & { [repeatedSymbol]: boolean }

	const refsProxy = new Proxy(refs, {
		get: (target, prop, receiver) => {
			if (prop === 'dataQuery' || prop === 'designQuery') {
				refsProxy[repeatedSymbol] = true
			}
			return Reflect.get(target, prop, receiver)
		},
	})

	const getRefsForDataItem = schemas ? createRefResolver(schemas) : () => null

	const repeatedNodes = _.mapValues(componentNodes, () => new Map<string, boolean>())
	const createTemplateCompNodes = (update: Update<any>, id: string, component?: Component) => {
		for (const compNodeName in componentNodes) {
			if (!component) {
				// delete
				update(COMPS_ROOT_NAMESPACE, compNodeName, id, undefined)
			} else {
				// Reset
				refsProxy[repeatedSymbol] = false
				const dependencies = componentNodes[compNodeName].getDependencies(component, refsProxy) || null
				repeatedNodes[compNodeName].set(id, refsProxy[repeatedSymbol])
				update(COMPS_ROOT_NAMESPACE, compNodeName, id, dependencies, dependencies)
			}
		}

		component?.modes?.definitions?.forEach((modeDefinition) => {
			update(
				COMPS_ROOT_NAMESPACE,
				'modes',
				modeDefinition.modeId,
				{ prefix: modeDefinition.type === 'DEFAULT' ? '[data-mode=default]' : '[data-mode=hover]' },
				EMPTY_SCHEMA
			)
		})
	}
	const createOverridesCompNodes = (update: Update<any>, id: string, context: string, component?: Component) => {
		const compId = createRepeatedItemOverrideId(id, context)
		for (const compNodeName in componentNodes) {
			if (!repeatedNodes[compNodeName].get(id)) {
				continue
			}

			if (!component) {
				// delete
				update(COMPS_ROOT_NAMESPACE, compNodeName, compId, undefined)
			} else {
				const dependencies = componentNodes[compNodeName].getDependencies(component, refs) || null
				update(COMPS_ROOT_NAMESPACE, compNodeName, compId, dependencies, dependencies)
			}
		}
	}

	const repeatedComponentOverrides = new Map<string, Partial<Component>>()

	const updateDataDependencies: UpdateDataDependencies = (update, type, id, dataItem) => {
		if (!dataItem) {
			update('data', type, id, undefined)
			return
		}
		const schemaRefs = getRefsForDataItem(dataItem, type)
		const customDeps = dataNodes[dataItem.type]?.getDependencies(dataItem)
		const dependencies = schemaRefs || customDeps ? { schema: schemaRefs, custom: customDeps } : null

		update('data', type, id, dependencies, dependencies)

		if (isRepeatedItemOverrideId(id)) {
			const overrides = { [mapNameToQuery[type]]: id }
			repeatedComponentOverrides.set(id, overrides)
		}
	}

	const updateData: Batched<UpdateData> = (update) => (type, id, dataItem) => {
		updateTrace('updateData', { type, id, dataItem })
		updateDataDependencies(update, type, id, dataStore.updateData(type, id, dataItem))
	}

	const updateSvgString: Batched<UpdateSvgString> = (update) => (id, svgString) => {
		updateTrace('updateSvgString', { id, svgString })
		update('svgStore', 'parsedSvg', id, parseSvgString(svgString), EMPTY_SCHEMA)
	}

	const updateDataItemIdToCompId = (component: Component | undefined) => {
		if (!component) {
			return
		}
		const { dataQuery, designQuery } = component
		if (dataQuery) {
			originalDataIdToCompId[dataQuery] = component.id
		}
		if (designQuery) {
			originalDataIdToCompId[designQuery] = component.id
		}
	}

	const updateStructure: Batched<UpdateStructure> = (update) => (id, component) => {
		updateTrace('updateStructure', { id, component })
		if (!component) {
			structureStore.removeById(id)
			createTemplateCompNodes(update, id)
			update('QA', 'fullNameCompType', id, undefined)
		} else {
			const fixedComponent = fixStructure(component)
			structureStore.updateById(id, fixedComponent)
			updateDataItemIdToCompId(fixedComponent)
			createTemplateCompNodes(update, id, fixedComponent)
			update('QA', 'fullNameCompType', id, component.componentType, EMPTY_SCHEMA)
		}
	}

	const updateEnvironment: Batched<UpdateEnvironment> = (update) => (environment) => {
		updateTrace('updateEnvironment', { environment })
		const {
			renderFlags,
			compIdToIgnoredTransformProps,
			ignoredTransformPropsForType,
			ooiCssConfigStore,
			clientSpecMap,
			...atoms
		} = environment
		for (const renderFlag in renderFlags) {
			update('environment', 'renderFlags', renderFlag, renderFlags[renderFlag], EMPTY_SCHEMA)
		}

		if (clientSpecMap) {
			const specMapByAppDefId = _(clientSpecMap).filter('appDefinitionId').keyBy('appDefinitionId').value()
			update('environment', 'atoms', 'clientSpecMap', specMapByAppDefId, EMPTY_SCHEMA)
		}

		for (const key in ooiCssConfigStore) {
			update('environment', 'ooiCssConfigStore', key, ooiCssConfigStore[key], EMPTY_SCHEMA)
		}

		for (const compId in compIdToIgnoredTransformProps) {
			update(
				'environment',
				'compIdToIgnoredTransformProps',
				compId,
				compIdToIgnoredTransformProps[compId],
				EMPTY_SCHEMA
			)
		}
		if (ignoredTransformPropsForType) {
			updateTwoLevelRecords(materializer)(
				update,
				['environment', 'ignoredTransformPropsForType'],
				ignoredTransformPropsForType
			)
		}
		for (const atom in atoms) {
			// @ts-expect-error
			update('environment', 'atoms', atom, atoms[atom], EMPTY_SCHEMA)
		}

		if (atoms.viewMode) {
			update('environment', 'mobile', 'isMobileView', atoms.viewMode === 'mobile', EMPTY_SCHEMA)
			const isNonAdaptiveMobile =
				!environment.isResponsive &&
				environment.deviceType === 'Smartphone' &&
				environment.viewMode === 'desktop'
			update('environment', 'mobile', 'isNonAdaptiveMobile', isNonAdaptiveMobile, EMPTY_SCHEMA)
			update('environment', 'mobile', 'deviceType', environment.deviceType, EMPTY_SCHEMA)
		}
		if (
			environment.experiments?.['specs.thunderbolt.compCssMappers_catharsis'] ||
			process.env.NODE_ENV === 'test'
		) {
			useNewDefaultToViewItem = true
		}
	}

	const transaction: Catharsis['transaction'] = (update, runAndReport = (fn) => fn()) => {
		const traceValue = traceEnabled && ({ actions: [], invalidations: [] } as any)
		if (traceValue) {
			catharsisTrace.push(traceValue)
		}
		const invalidations = runAndReport(
			() =>
				materializer.batch((batchUpdate) => {
					runAndReport(
						() =>
							update({
								updateData: updateData(batchUpdate),
								updateStructure: updateStructure(batchUpdate),
								updateEnvironment: updateEnvironment(batchUpdate),
								updateSvgString: updateSvgString(batchUpdate),
							}),
						'materializer.update'
					)

					// Add virtual structure for the overrides data items
					if (repeatedComponentOverrides.size) {
						runAndReport(
							() =>
								repeatedComponentOverrides.forEach((overrides, id) => {
									const [dataId, context] = getTemplateIdAndItemContext(id)
									const compId = originalDataIdToCompId[dataId]
									const contextStore = structureStore.getChildStore(context)
									contextStore.updateById(compId, overrides)
									const component = contextStore.getById(compId)
									createOverridesCompNodes(batchUpdate, compId, context, component)
								}),
							'repeatedComponentOverrides.forEach'
						)
						runAndReport(() => repeatedComponentOverrides.clear(), 'repeatedComponentOverrides.clear()')
					}
				}, runAndReport),
			'materializer.batch'
		) as Array<Array<string>>

		runAndReport(() => {
			for (const invalidation of invalidations) {
				if (traceValue) {
					traceValue?.invalidations.push({ path: invalidation, value: materializer.get(invalidation) })
				}
				onInvalidation(invalidation, materializer.get)
			}
		}, 'onInvalidation')
	}

	const trace = {
		start: () => {
			if (!traceEnabled) {
				traceEnabled = true
				catharsisTrace.length = 0
			}
		},
		end: () => {
			traceEnabled = false
		},
		clear: () => {
			catharsisTrace.length = 0
		},
		get: () => catharsisTrace,
	}

	return {
		transaction,
		trace,
	}
}
